import {
  FileColumnAliases,
  FileColumnEnum,
  FileColumnLabel,
  MAX_COST_PER_UNIT,
  MAX_IMPORT_ROWS,
  MAX_QUANTITY,
} from "./stock-adjustment-file-constants";

const TEXT_NUMBER = /^\d+(?:[.,]\d{1,2})?$/;

const FORMAT_MESSAGE =
  "Tulis angka saja, tanpa pemisah ribuan atau simbol, contoh: 15000 atau 15000,50";

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const normalizeHeader = (value) =>
  String(value ?? "")
    .replace(/^\uFEFF/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const roundCents = (value) => Math.round(value * 100) / 100;

/**
 * A number cell as `{ blank: true }`, `{ value }` or `{ error }`.
 *
 * A numeric cell (xlsx) is trusted as it is. A text cell (every CSV cell, or an xlsx cell stored as
 * text) is only a number when it is plain digits with at most one decimal separator and one or two
 * digits after it. Text like `1.000` or `15,000` is refused rather than guessed: it could mean one
 * thousand or one, and the wrong guess changes the stock or the value by a factor of a thousand.
 */
export const parseNumberCell = (value) => {
  if (isBlank(value)) return { blank: true };

  if (typeof value === "number") {
    return Number.isFinite(value) ? { value } : { error: FORMAT_MESSAGE };
  }

  if (typeof value === "string") {
    const text = value.trim();
    if (!TEXT_NUMBER.test(text)) return { error: FORMAT_MESSAGE };
    return { value: Number(text.replace(",", ".")) };
  }

  return { error: FORMAT_MESSAGE };
};

const parseSku = (value) => {
  if (isBlank(value)) return "";
  return typeof value === "number" ? String(value) : String(value).trim();
};

const parseQuantity = (value) => {
  const cell = parseNumberCell(value);
  if (cell.blank) return { error: "Qty wajib diisi" };
  if (cell.error) return { error: `Qty: ${cell.error}` };
  if (!Number.isInteger(cell.value) || cell.value <= 0) {
    return { error: "Qty harus berupa bilangan bulat lebih dari 0" };
  }
  if (cell.value > MAX_QUANTITY) {
    return { error: "Qty terlalu besar (maksimal 9.999.999.999)" };
  }
  return { value: cell.value };
};

const parseCost = (value) => {
  const cell = parseNumberCell(value);
  if (cell.blank) return { error: "Harga per Unit wajib diisi" };
  if (cell.error) return { error: `Harga per Unit: ${cell.error}` };
  if (cell.value < 0) return { error: "Harga per Unit tidak boleh negatif" };
  // The cost is stored to the cent; round here so the figure shown is the figure saved.
  const cost = roundCents(cell.value);
  if (cost > MAX_COST_PER_UNIT) {
    return { error: "Harga per Unit terlalu besar (maksimal 99.999.999,99)" };
  }
  return { value: cost };
};

/** The header row (the first row with anything in it) and where each wanted column sits. */
const locateColumns = (sheetRows, wanted) => {
  const headerIndex = sheetRows.findIndex(({ cells }) => !cells.every(isBlank));
  if (headerIndex === -1) {
    return { fileError: "File tidak berisi data." };
  }

  const headerCells = sheetRows[headerIndex].cells.map(normalizeHeader);
  const columnIndex = {};
  const missing = [];
  for (const column of wanted) {
    const index = headerCells.findIndex((cell) =>
      FileColumnAliases[column].includes(cell)
    );
    if (index === -1) missing.push(FileColumnLabel[column]);
    else columnIndex[column] = index;
  }

  if (missing.length > 0) {
    const expected = wanted.map((column) => FileColumnLabel[column]).join(" | ");
    return {
      fileError: `Kolom ${missing.join(", ")} tidak ditemukan. Gunakan template dengan kolom: ${expected}.`,
    };
  }

  // Columns past the last header cell have no name. A value there usually means a decimal comma
  // split a number in a comma-separated file: `A-1,5,15,5` reads as Harga 15 plus a stray 5.
  const rawHeader = sheetRows[headerIndex].cells;
  let headerWidth = rawHeader.length;
  while (headerWidth > 0 && isBlank(rawHeader[headerWidth - 1])) headerWidth -= 1;

  return { columnIndex, headerWidth, dataRows: sheetRows.slice(headerIndex + 1) };
};

const parseRows = (sheetRows, { columns, readRow }) => {
  const located = locateColumns(sheetRows, columns);
  if (located.fileError) return { lines: [], errors: [], fileError: located.fileError };

  const { columnIndex, headerWidth, dataRows } = located;
  const lines = [];
  const errors = [];
  const firstRowBySku = new Map();

  for (const { row, cells } of dataRows) {
    if (cells.every(isBlank)) continue; // an empty row is skipped, not an error

    const sku = parseSku(cells[columnIndex[FileColumnEnum.SKU]]);
    const problems = [];
    if (!sku) problems.push("SKU wajib diisi");
    if (cells.slice(headerWidth).some((cell) => !isBlank(cell))) {
      problems.push(
        'Baris berisi lebih banyak kolom daripada judul. Di file CSV, angka desimal dengan koma harus diberi tanda kutip ("15000,50") atau memakai titik (15000.50)'
      );
    }

    const parsed = readRow(cells, columnIndex, problems);

    if (sku) {
      const firstRow = firstRowBySku.get(sku);
      if (firstRow !== undefined) {
        problems.push(
          `SKU sudah ada di baris ${firstRow}. Satu SKU hanya boleh satu baris, gabungkan menjadi satu baris`
        );
      } else {
        firstRowBySku.set(sku, row);
      }
    }

    if (problems.length > 0) {
      errors.push({ row, sku, message: problems.join("; ") });
    } else {
      lines.push({ row, sku, ...parsed });
    }
  }

  if (lines.length + errors.length === 0) {
    return { lines, errors, fileError: "File tidak berisi baris data." };
  }
  if (lines.length + errors.length > MAX_IMPORT_ROWS) {
    return {
      lines: [],
      errors: [],
      fileError: `File berisi lebih dari ${MAX_IMPORT_ROWS.toLocaleString("id-ID")} baris. Pecah menjadi beberapa file.`,
    };
  }

  return { lines, errors, fileError: null };
};

/**
 * `SKU | Qty | Harga per Unit` rows of a new Penyesuaian Stok. Returns the good lines
 * (`{ row, sku, quantity, costPerUnit }`) and one error per bad row (`{ row, sku, message }`),
 * every problem of the row in its message. Nothing is sent anywhere from here.
 */
export const parseImportRows = (sheetRows) =>
  parseRows(sheetRows, {
    columns: [
      FileColumnEnum.SKU,
      FileColumnEnum.QUANTITY,
      FileColumnEnum.COST_PER_UNIT,
    ],
    readRow: (cells, columnIndex, problems) => {
      const quantity = parseQuantity(cells[columnIndex[FileColumnEnum.QUANTITY]]);
      const cost = parseCost(cells[columnIndex[FileColumnEnum.COST_PER_UNIT]]);
      if (quantity.error) problems.push(quantity.error);
      if (cost.error) problems.push(cost.error);
      return { quantity: quantity.value, costPerUnit: cost.value };
    },
  });

/** `SKU | Harga per Unit` rows for re-costing an existing Penyesuaian Stok. */
export const parseCostUpdateRows = (sheetRows) =>
  parseRows(sheetRows, {
    columns: [FileColumnEnum.SKU, FileColumnEnum.COST_PER_UNIT],
    readRow: (cells, columnIndex, problems) => {
      const cost = parseCost(cells[columnIndex[FileColumnEnum.COST_PER_UNIT]]);
      if (cost.error) problems.push(cost.error);
      return { costPerUnit: cost.value };
    },
  });
